/**
 * Image slot manifest.
 *
 * All photography is owner-supplied from a personal library of real Maryland
 * horse photography (spec section 11, CLAUDE.md hard rule 3). Nothing in this
 * repository sources, generates, or embeds a photograph. Every slot below
 * describes the *intended subject* so the right frame can be chosen from the
 * library; until a file exists the component renders a labeled placeholder.
 *
 * Dropping a real photo in is two steps and nothing else:
 *   1. Put the file in `public/images/` at the slot's `plannedPath`.
 *   2. Set `src: '<plannedPath>'` on that slot here.
 *
 * Everything else is already wired and needs no code change (spec section 18):
 *
 * - `width` and `height` are rendered as explicit attributes so the browser
 *   reserves the box before the bytes arrive (CLS budget 0.1);
 * - a slot marked `eager` renders with `fetchpriority="high"` and
 *   `loading="eager"` (components/media/PhotoSlot.tsx);
 * - and the server adds a `<link rel="preload" as="image" fetchpriority="high">`
 *   for every `eager` slot that has a `src`, into the HTML it serves
 *   (src/server/app.ts). Preloading only happens for the slot that is the LCP
 *   element, which is what "Preload only if it is the LCP element" means.
 *
 * ## Why this lives in `shared/`
 *
 * It is client content — components render it — but the server reads it too, to
 * emit those preload links into the document before any JavaScript runs, which
 * is the only point at which a preload is worth anything. It is inert data with
 * no imports, so both sides can hold it without either owning the other.
 */

/**
 * Copy shown inside an empty slot. It has to be legible enough to demo and
 * obvious enough that nobody mistakes it for a finished page.
 */
export const photoSlotPlaceholder = {
  eyebrow: 'Photo slot',
  unsuppliedLabel: 'Owner-supplied photograph, not yet added',
  sizeLabel: 'Delivered size',
  subjectLabel: 'Subject',
  /** `{path}` is the slot's plannedPath. */
  dropInHint: 'Drop the file at {path}, then set src on this slot in shared/content/images.ts',
} as const;

/** How dark the gradient over a hero photo needs to be for text to stay legible. */
export type ScrimStrength = 'strong' | 'medium';

export interface ImageSlot {
  /** Stable identity, shown on the placeholder and used in the README table. */
  id: string;
  /** Which page the slot appears on, for the README table and for audits. */
  page: 'home' | 'owners' | 'providers' | 'global';
  /** Where on that page it sits. */
  position: string;
  /** Documentary subject to look for in the library. Described, never embedded. */
  subject: string;
  /**
   * Alt text used once `src` is set. Empty means decorative: the headline
   * beside the photo carries the meaning, so a screen reader gains nothing from
   * a description (spec section 18). If a supplied photo turns out to be
   * informative, write alt text here.
   */
  alt: string;
  /** Intrinsic width of the delivered file, in pixels. */
  width: number;
  /** Intrinsic height of the delivered file, in pixels. */
  height: number;
  /** CSS `aspect-ratio` value for the reserved box. */
  aspectRatio: string;
  /** How the frame should survive a narrow viewport. */
  mobileCrop?: string;
  /** Overlay strength for slots that carry text on top. */
  scrim?: ScrimStrength;
  /** Where the file goes, relative to the site root. */
  plannedPath: string;
  /** Set this to `plannedPath` once the file exists. Undefined = not supplied. */
  src?: string;
  /** Delivery format guidance for the owner. */
  format: string;
  /** Target transferred size. Over this and the LCP budget is at risk. */
  targetFileSize: string;
  /**
   * True for the one slot that is the LCP element of its page. Drives eager
   * loading and `fetchpriority="high"` on the image, and the server-side
   * preload link — but only once `src` is set. An empty slot preloads nothing.
   */
  eager?: boolean;
}

const HERO_WIDTH = 2400;
const HERO_HEIGHT = 1350;
const BAND_WIDTH = 2400;
const BAND_HEIGHT = 1000;

const HERO_FORMAT = 'WebP preferred, JPEG acceptable';
const HERO_SIZE = '≤ 300 KB after compression';
const BAND_SIZE = '≤ 250 KB after compression';

/**
 * Written as a literal first so the keys stay literal (`ImageSlotId` is derived
 * from them), then re-exported through the interface so consumers see every
 * optional field.
 */
const slots = {
  'hero-home': {
    id: 'hero-home',
    page: 'home',
    position: 'Homepage hero, full bleed behind the headline',
    subject:
      'Wide barn aisle or farm yard with work visibly underway — a truck backed up to a barn, tools out, a horse being handled. Ordinary working Maryland, not a show ring.',
    alt: '',
    width: HERO_WIDTH,
    height: HERO_HEIGHT,
    aspectRatio: '16 / 9',
    mobileCrop:
      'Must survive a 4:5 crop on phones. Keep the subject off-center-left and the lower third uncluttered — the headline sits there.',
    scrim: 'strong',
    plannedPath: '/images/hero-home.webp',
    src: '/images/hero-home.webp',
    format: HERO_FORMAT,
    targetFileSize: HERO_SIZE,
    eager: true,
  },
  'hero-owners': {
    id: 'hero-owners',
    page: 'owners',
    position: 'Owner page hero, full bleed behind the headline',
    subject:
      'An owner or caretaker with their horse in an everyday moment — leading in from turnout, holding for the farrier, hosing off after work. Care, not competition.',
    alt: '',
    width: HERO_WIDTH,
    height: HERO_HEIGHT,
    aspectRatio: '16 / 9',
    mobileCrop: 'Must survive a 4:5 crop on phones. Keep the lower third clear for the headline.',
    scrim: 'strong',
    plannedPath: '/images/hero-owners.webp',
    src: '/images/hero-owners.webp',
    format: HERO_FORMAT,
    targetFileSize: HERO_SIZE,
    eager: true,
  },
  'hero-providers': {
    id: 'hero-providers',
    page: 'providers',
    position: 'Provider page hero, full bleed behind the headline',
    subject:
      'A professional mid-job: farrier at the anvil or under a hoof, vet with a bag open, hauler loading a trailer. Hands and tools in frame, face optional.',
    alt: '',
    // Delivered smaller than the 2400-wide guideline: this frame is grass and
    // coat texture edge to edge, which WebP compresses poorly — 2400px was
    // 464 KB at acceptable quality. 1792px lands exactly on the 300 KB LCP
    // budget and sits behind a strong scrim under the headline.
    width: 1792,
    height: 1008,
    aspectRatio: '16 / 9',
    mobileCrop: 'Must survive a 4:5 crop on phones. Keep the lower third clear for the headline.',
    scrim: 'strong',
    plannedPath: '/images/hero-providers.webp',
    src: '/images/hero-providers.webp',
    format: HERO_FORMAT,
    targetFileSize: HERO_SIZE,
    eager: true,
  },
  'band-home-1': {
    id: 'band-home-1',
    page: 'home',
    position: 'Homepage, between the problem section and the product preview',
    subject:
      'Hands at work in close detail — rasp on a hoof, a bridle being mended, hay being moved. No faces needed.',
    alt: '',
    width: BAND_WIDTH,
    height: BAND_HEIGHT,
    aspectRatio: '12 / 5',
    mobileCrop: 'Crops to roughly 3:2 on phones from the center.',
    plannedPath: '/images/band-home-1.webp',
    src: '/images/band-home-1.webp',
    format: HERO_FORMAT,
    targetFileSize: BAND_SIZE,
  },
  'band-home-2': {
    id: 'band-home-2',
    page: 'home',
    position: 'Homepage, between the community section and the closing call to action',
    subject:
      'Maryland landscape read at a glance — board fence, rolling pasture, a modest barn. A different facility type from the hero on purpose.',
    alt: '',
    width: BAND_WIDTH,
    height: BAND_HEIGHT,
    aspectRatio: '12 / 5',
    mobileCrop: 'Crops to roughly 3:2 on phones from the center.',
    plannedPath: '/images/band-home-2.webp',
    src: '/images/band-home-2.webp',
    format: HERO_FORMAT,
    targetFileSize: BAND_SIZE,
  },
  'band-owners-1': {
    id: 'band-owners-1',
    page: 'owners',
    position: 'Owner page, between the problem section and the product preview',
    subject:
      'Service work happening at a barn an owner would recognize — a vet truck in the aisle, a farrier rig with the doors open, a trailer being loaded.',
    alt: '',
    width: BAND_WIDTH,
    height: BAND_HEIGHT,
    aspectRatio: '12 / 5',
    mobileCrop: 'Crops to roughly 3:2 on phones from the center.',
    plannedPath: '/images/band-owners-1.webp',
    src: '/images/band-owners-1.webp',
    format: HERO_FORMAT,
    targetFileSize: BAND_SIZE,
  },
  'band-providers-1': {
    id: 'band-providers-1',
    page: 'providers',
    position: 'Provider page, between the why-now section and the benefits grid',
    subject:
      'A working rig or a business at work — lettered truck, laid-out tools, a tailgate desk. It should read as a small business, not a brand shoot.',
    alt: '',
    // Delivered at 2000px: sky and grass texture priced 2400px over the
    // 250 KB band budget (same trade as hero-providers, documented there).
    width: 2000,
    height: 833,
    aspectRatio: '12 / 5',
    mobileCrop: 'Crops to roughly 3:2 on phones from the center.',
    plannedPath: '/images/band-providers-1.webp',
    src: '/images/band-providers-1.webp',
    format: HERO_FORMAT,
    targetFileSize: BAND_SIZE,
  },
  'social-share': {
    id: 'social-share',
    page: 'global',
    position: 'Open Graph and Twitter card image for every page. Never rendered on the site.',
    subject:
      'One strong documentary frame with room for nothing else — it is shown small and cropped by other platforms. A wordmark overlay can be added by the owner.',
    alt: '',
    width: 1200,
    height: 630,
    aspectRatio: '40 / 21',
    plannedPath: '/images/social-share.jpg',
    src: '/images/social-share.jpg',
    format: 'JPEG or PNG. Some platforms will not fetch WebP.',
    targetFileSize: '≤ 300 KB',
  },
} satisfies Record<string, ImageSlot>;

export type ImageSlotId = keyof typeof slots;

export const imageSlots: Record<ImageSlotId, ImageSlot> = slots;

/** Every slot, for the README table and for the audit tests. */
export const imageSlotList: readonly ImageSlot[] = Object.values(imageSlots);

/**
 * The path Open Graph metadata points at. Documented and reserved; the file
 * itself is owner-supplied and is not in the repository yet.
 */
export const socialShareImagePath = slots['social-share'].plannedPath;
